/** Reading a catalog entry. Every answer comes off the manifest; no table decides anything. */

import type {
  CatalogEntry,
  NamedCanvas,
  NamedExport,
  NamedRequirement,
  NamedTrigger,
  SettingField,
  SettingValue,
} from "../types";

/** A bundle is an activity whose manifest lists members. */
export function isBundle(entry: CatalogEntry): boolean {
  return entry.manifest.bundle != null;
}

export function kindWord(entry: CatalogEntry): string {
  return isBundle(entry) ? "bundle" : "activity";
}

export function lifecycleOf(entry: CatalogEntry): string {
  return entry.manifest.lifecycle ?? "episodic";
}

export function canvasesOf(entry: CatalogEntry): NamedCanvas[] {
  return Object.entries(entry.manifest.canvases ?? {}).map(([kind, decl]) => ({ ...decl, kind }));
}

export function requirementsOf(entry: CatalogEntry): NamedRequirement[] {
  return Object.entries(entry.manifest.requires ?? {}).map(([name, req]) => ({ ...req, name }));
}

export function providesOf(entry: CatalogEntry): string[] {
  return Object.keys(entry.manifest.provides ?? {});
}

/** Every role any canvas of this activity gates on. */
export function rolesOf(entry: CatalogEntry): string[] {
  const seen: string[] = [];
  for (const canvas of canvasesOf(entry)) {
    for (const role of canvas.roles ?? []) if (!seen.includes(role)) seen.push(role);
  }
  return seen;
}

export function exportsOf(entry: CatalogEntry): NamedExport[] {
  return Object.entries(entry.manifest.events?.exports ?? {}).map(([name, decl]) => {
    const fields = Object.entries(decl.fields ?? {});
    const canvases: string[] = [];
    for (const [, field] of fields) {
      for (const kind of field.visible ?? []) if (!canvases.includes(kind)) canvases.push(kind);
    }
    return { name, version: decl.version ?? 1, fields: fields.map(([key]) => key), canvases };
  });
}

export function triggersOf(entry: CatalogEntry): NamedTrigger[] {
  return Object.entries(entry.manifest.events?.triggers ?? {}).map(([name, decl]) => ({
    name,
    params: Object.entries(decl.parameters ?? {}).map(([key, parameter]) => ({
      name: key,
      type: parameter.type,
      required: parameter.required !== false,
    })),
  }));
}

export function settingsOf(entry: CatalogEntry): SettingField[] {
  return entry.settings;
}

/** The settings an added activity starts with. */
export function defaultSettings(entry: CatalogEntry): Record<string, SettingValue> {
  const out: Record<string, SettingValue> = {};
  for (const field of entry.settings) out[field.key] = field.default;
  return out;
}

/** The canvas kinds this entry declares, deduplicated and in declaration order. */
export function canvasKindsOf(entry: CatalogEntry): string[] {
  return canvasesOf(entry).map((canvas) => canvas.kind);
}

/** Text every search over the catalog reads. */
export function catalogText(entry: CatalogEntry): string {
  return [
    entry.name,
    entry.activity_id,
    entry.description ?? "",
    entry.publisher,
    lifecycleOf(entry),
    canvasKindsOf(entry).join(" "),
    requirementsOf(entry)
      .map((req) => req.type)
      .join(" "),
  ].join(" ");
}
